import { useState, type RefObject } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import logoAirflow from "@/assets/logo-airflow.png";
import logoTibco from "@/assets/logo-tibco.png";
import logoDenodo from "@/assets/logo-denodo-1.png";
import logoGCP from "@/assets/logo-gcp.png";
import logoNutanix from "@/assets/logo-nutanix.png";
import logoGithub from "@/assets/logo-github.png";
import logoGit from "@/assets/logo-git.png";
import logoFigma from "@/assets/logo-figma-new.png";
import logoPentaho from "@/assets/logo-pentaho-new.png";
import logoAlteryx from "@/assets/logo-alteryx-4.png";
import logoDataiku from "@/assets/logo-dataiku.png";
import logoConfluent from "@/assets/logo-confluent.jpg";
import logoFivetran from "@/assets/logo-fivetran.png";
import logoEDB from "@/assets/logo-edb.jpg";
import logoAstronomer from "@/assets/logo-astronomer.png";
import logoTalend from "@/assets/logo-talend.png";
import logoLinux from "@/assets/logo-linux.png";
import logoDocker from "@/assets/logo-docker.png";
import logoVercel from "@/assets/logo-vercel-new.png";
import logoMongoDB from "@/assets/logo-mongodb.png";
import logoSnowflake from "@/assets/logo-snowflake.png";
import logoOracleDB from "@/assets/logo-oracle-db.png";
import logoPostgres from "@/assets/logo-postgres.png";
import logoEnterpriseDB from "@/assets/logo-enterprisedb.png";
import logoAWS from "@/assets/logo-aws.png";
import logoVite from "@/assets/logo-vite.png";
import logoReact from "@/assets/logo-react.png";
import logoTypescript from "@/assets/logo-typescript.png";
import logoTailwind from "@/assets/logo-tailwind.png";
import logoSupabase from "@/assets/logo-supabase.png";
import logoShadcn from "@/assets/logo-shadcn.png";
import logoHTML from "@/assets/logo-html.png";
import logoCSS from "@/assets/logo-css.png";
import logoJavaScript from "@/assets/logo-javascript.png";
import logoNetlify from "@/assets/logo-netlify.png";

const Tools = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("data-engineer");
  const [pausedRow, setPausedRow] = useState<string | null>(null);
  
  // Data Engineer Tools - always active
  const dataEngineerScroll1 = useInfiniteScroll(1.2, pausedRow === "de-row-1");
  const dataEngineerScroll2 = useInfiniteScroll(-1.2, pausedRow === "de-row-2");
  const dataEngineerScroll3 = useInfiniteScroll(1.2, pausedRow === "de-row-3");

  // Fullstack Tools - always active
  const fullstackScroll1 = useInfiniteScroll(1.2, pausedRow === "fs-row-1");

  const dataEngineerRow1 = [
    { name: "Apache Airflow", logo: logoAirflow },
    { name: "TIBCO", logo: logoTibco },
    { name: "Talend", logo: logoTalend },
    { name: "Google Cloud Platform", logo: logoGCP },
    { name: "AWS", logo: logoAWS },
    { name: "Alteryx", logo: logoAlteryx },
    { name: "Dataiku", logo: logoDataiku },
    { name: "Denodo", logo: logoDenodo },
  ];

  const dataEngineerRow2 = [
    { name: "Git", logo: logoGit },
    { name: "GitHub", logo: logoGithub },
    { name: "Pentaho", logo: logoPentaho },
    { name: "Linux", logo: logoLinux },
    { name: "Docker", logo: logoDocker },
    { name: "PostgreSQL", logo: logoPostgres },
  ];

  const dataEngineerRow3 = [
    { name: "MongoDB", logo: logoMongoDB },
    { name: "Snowflake", logo: logoSnowflake },
    { name: "Oracle", logo: logoOracleDB },
    { name: "EDB", logo: logoEnterpriseDB },
    { name: "Confluent", logo: logoConfluent },
    { name: "Fivetran", logo: logoFivetran },
    { name: "Nutanix", logo: logoNutanix },
  ];

  const fullstackRow1 = [
    { name: "HTML", logo: logoHTML },
    { name: "CSS", logo: logoCSS },
    { name: "JavaScript", logo: logoJavaScript },
    { name: "Vite", logo: logoVite },
    { name: "React", logo: logoReact },
    { name: "TypeScript", logo: logoTypescript },
    { name: "Tailwind CSS", logo: logoTailwind },
    { name: "Supabase", logo: logoSupabase },
    { name: "Shadcn UI", logo: logoShadcn },
    { name: "Vercel", logo: logoVercel },
    { name: "Netlify", logo: logoNetlify },
    { name: "Figma", logo: logoFigma },
  ];

  const renderToolRow = (tools: { name: string; logo: string }[], scrollRef: RefObject<HTMLDivElement>, scrollPosition: number, rowId: string) => (
    <div 
      className="relative overflow-hidden"
      onMouseEnter={() => setPausedRow(rowId)}
      onMouseLeave={() => setPausedRow(null)}
    >
      <div 
        ref={scrollRef}
        className="flex gap-12 will-change-transform"
        style={{ transform: `translateX(-${scrollPosition}px)` }}
      >
        {[...tools, ...tools, ...tools].map((tool, index) => (
          <div
            key={`${tool.name}-${index}`}
            className="flex-shrink-0 flex flex-col items-center gap-4 w-32 hover-scale-105 transition-smooth"
          >
            <div className="w-24 h-24 flex items-center justify-center bg-card rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
              <img
                src={tool.logo}
                alt={tool.name}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-sm text-center text-muted-foreground font-medium">
              {tool.name}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="tools" className="py-20 overflow-hidden relative">
      <div className="container mx-auto px-4 relative z-10">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-foreground">
          {t.tools.title}
        </h2>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
            <TabsTrigger value="data-engineer">Data Engineer</TabsTrigger>
            <TabsTrigger value="fullstack">Fullstack</TabsTrigger>
          </TabsList>

          <TabsContent 
            value="data-engineer" 
            className={cn(
              "space-y-12",
              activeTab !== "data-engineer" && "hidden"
            )}
            forceMount
          >
            {renderToolRow(dataEngineerRow1, dataEngineerScroll1.containerRef, dataEngineerScroll1.position, "de-row-1")}
            {renderToolRow(dataEngineerRow2, dataEngineerScroll2.containerRef, dataEngineerScroll2.position, "de-row-2")}
            {renderToolRow(dataEngineerRow3, dataEngineerScroll3.containerRef, dataEngineerScroll3.position, "de-row-3")}
          </TabsContent>

          <TabsContent 
            value="fullstack" 
            className={cn(
              "space-y-12",
              activeTab !== "fullstack" && "hidden"
            )}
            forceMount
          >
            {renderToolRow(fullstackRow1, fullstackScroll1.containerRef, fullstackScroll1.position, "fs-row-1")}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Tools;
